import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Avatar,
  Chip,
} from "@mui/material";
import { postApiVenueBased, postSaveVenueDocument } from "../../Core/Apicall";
import { useNavigate } from "react-router-dom";
import CommonPagination from "../../Components/CommonPagination/CommonPagination";
import EmptyData from "../../Components/EmptyData/EmptyData";
import CommonShimmer from "../../Components/CommonShimmer/CommonShimmer";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CustomUploadProfilePic } from "../../../src/Core/CustomUploadProfilePic";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

function Venues() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [file, setFile] = useState(null);

  const isProfileData = useSelector((state) => state.isProfileData);
  const navigate = useNavigate();
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const [preview, setPreview] = useState(null);


  // 🔹 Fetch Venues
  const fetchVenues = async () => {
    setLoading(true);
    try {
      const response = await postApiVenueBased({
        search,
        offset: page,
        limit: rowsPerPage,
        userId: isProfileData?.data?.userId,
      });

      if (Array.isArray(response?.data)) {
        setData(response.data);
        setTotalItems(response?.totalCount || response?.data?.length || 0);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching venues:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues();
  }, [page, rowsPerPage, search]);

  // 🔹 Handle Upload Dialog
  const handleOpenUpload = (venue) => {
    setSelectedVenue(venue);
    setUploadDialogOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      toast.error("Please select an image file first!");
      return;
    }
    try {
      const result = await CustomUploadProfilePic(file, "profile");
      console.log("result", result);

      if (result && result.location) {
        setFile(result.location);
        setPreview(result.location)
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (error) {
      toast.error(
        error || "Something went wrong while uploading. Please try again later."
      );
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("Please select a file!");

    try {
      const params = {
        userId: isProfileData?.data?.userId,
        venueId: selectedVenue._id,
        documentType: "Image",
        documentPath: file,
      };

      const response = await postSaveVenueDocument(params);

      console.log("response", response);

      setUploadDialogOpen(false);
      setSelectedVenue(null);
      setFile(null);
    } catch (err) {
      console.error(err);
      alert("❌ Upload failed due to network error");
    }
  };

  return (
    <Box sx={{ paddingBottom: "20px", overflow: 'auto' }}>
      {/* 🔍 Search */}
      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white", width: 300 }}
          placeholder="Search venue..."
        />
      </Box>

      {/* 🧾 Venue Table */}
      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{ borderRadius: 3, overflow: "hidden" }}
      >
        <TableContainer className="scroll-bar">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Venue</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                {/* <TableCell>State</TableCell>
                <TableCell>Country</TableCell> */}
                <TableCell>Approval Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {!loading && (
              <TableBody>
                {data.map((item) => (
                  <TableRow hover key={item._id}>
                    {/* 🏢 Venue */}
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          src={
                            item?.coverImage?.thumbnailUrl ||
                            item?.coverImage?.url ||
                            "https://via.placeholder.com/50"
                          }
                          alt={item?.venueName}
                          sx={{ width: 45, height: 45 }}
                        />
                        <Box
                          sx={{ cursor: "pointer", fontWeight: 500 }}
                          onClick={() =>
                            navigate("/admin/VenueDetails", {
                              state: { venue: item },
                            })
                          }
                        >
                          {item?.venueName ? item.venueName.slice(0, 10) + "..." : "-"}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Type */}
                    <TableCell>{item?.venueType || "-"}</TableCell>

                    {/* Phone */}
                    <TableCell>
                      {item?.phoneCountryCode} {item?.phoneNumber || "-"}
                    </TableCell>

                    {/* City / State / Country */}
                    <TableCell>{item?.cityOrCounty || "-"},{item?.stateOrRegion || "-"},{item?.country || "-"}</TableCell>
                    {/* <TableCell>{item?.stateOrRegion || "-"}</TableCell>
                    <TableCell>{item?.country || "-"}</TableCell> */}

                    {/* Approval Status */}
                    <TableCell>
                      <Chip
                        label={
                          item?.approvalStatus == "NotUploaded"
                            ? "Not Uploaded"
                            : item?.approvalStatus ?? "Unknown"
                        }
                        color={
                          item?.approvalStatus === "Approved"
                            ? "success"
                            : item?.approvalStatus === "Rejected"
                              ? "error"
                              : item?.approvalStatus === "Pending"
                                ? "warning"
                                : "default"
                        }
                        size="small"
                      />
                    </TableCell>

                    {/* ✅ Action Button Logic */}
                    <TableCell>
                      {/* 📄 Not Uploaded */}
                      {item?.approvalStatus === "NotUploaded" && (
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ backgroundColor: "black" }}
                          size="small"
                          onClick={() => handleOpenUpload(item)}
                        >
                          Upload Document
                        </Button>
                      )}

                      {/* 🕒 Pending */}
                      {item?.approvalStatus === "Pending" && (
                        <Chip
                          label="Pending Approval"
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      )}

                      {/* ❌ Rejected */}
                      {item?.approvalStatus === "Rejected" && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleOpenUpload(item)}
                        >
                          Re-upload
                        </Button>
                      )}

                      {/* ✅ Approved */}
                      {item?.approvalStatus === "Approved" && (
                        <Chip
                          label="Approved"
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          {/* ⏳ Loading */}
          {loading && (
            <Box sx={{ p: 2 }}>
              <CommonShimmer />
            </Box>
          )}
        </TableContainer>

        {/* 📭 Empty State */}
        {!loading && <EmptyData data={data} />}

        {/* 🔢 Pagination */}
        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={(value) => setPage(value)}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />
      </Paper>

      {/* 📤 Upload Dialog */}
      {/* <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      >
        <DialogTitle>
          Upload Document for {selectedVenue?.venueName}
        </DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept="image/*,.pdf,.docx"
            onChange={handleFileChange}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedVenue(null);
              setFile(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUpload} variant="contained" color="primary">
            Upload
          </Button>
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setFile(null);
          setPreview(null);
        }}
      >
        <DialogTitle>
          Upload Document for {selectedVenue?.venueName}
        </DialogTitle>

        <DialogContent>
          {/* Upload Box */}
          <label
            htmlFor="fileInput"
            className="flex flex-col items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-xl p-6 cursor-pointer hover:border-blue-500 transition duration-200"
            style={{ gap: "10px",alignItems:"center",justifyContent:"center" }}
          >
            <CloudUploadIcon sx={{ fontSize: 60 }} />
            <p className="mt-2 text-gray-700 font-medium">Click to upload</p>
          </label>

          {/* Hidden Input */}
          <input
            id="fileInput"
            type="file"
            accept="image/*,.pdf,.docx"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {/* Preview */}
          {preview && (
            <img
              src={preview}
              alt="Preview"
              style={{width:"100%",height:"100px",objectFit:'cover'}}
              className="mt-4 w-full max-h-64 object-contain rounded-lg"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setFile(null);
              setPreview(null);
            }}
            style={{ color: "black" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpload}
            variant="contained"
            style={{ backgroundColor: "black" }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Venues;
